// @ts-nocheck
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Async from 'components/Async';
import { Scorm12API, Scorm2004API } from 'scorm-again';

import { IScormModel } from '@common/models/Chunks/Scorm';

import {
	FullScreenButton,
	ModalElement,
	OrientationOverlay,
	OrientationOverlayText,
	ScormIframe,
	StyledScormWrapper,
	Wrapper,
	WrapperIframe,
} from './styled';
import clearScormLocalStorage from './utils/clearScormLocalStorage';

type ScormWrapperProps = {
	onCommit?: (data: any) => void;
	fetchData?: () => Promise<void>;
	showReloadButton?: (data: boolean) => void;
	scorm: IScormModel;
	logLevel?: number;
	modified?: number;
	withHeader?: boolean;
	isAdminEnv?: boolean;
	isLarge?: boolean;
	enableOrientationLock?: boolean;
	orientationHint?: React.ReactNode;
};

// Tweak for correct SCORM rendering through all browsers
export const OUTER_IFRAME_DOC = `
<!DOCTYPE html>
<html>
  <head>
    <style>
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: transparent;
      }
    </style>
  </head>
  <body></body>
</html>
`;

function ScormWrapper({
	onCommit,
	fetchData,
	showReloadButton,
	scorm,
	modified = 0,
	logLevel = 4,
	withHeader = false,
	isAdminEnv = false,
	isLarge = false,
	enableOrientationLock = false,
	orientationHint,
}: ScormWrapperProps) {
	const [loading, setLoading] = useState(true);
	const [contentRef, setContentRef] = useState();
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [isPortrait, setIsPortrait] = useState(false);
	const [updateKey, setUpdateKey] = useState(modified);
	const [isDataLoaded, setIsDataLoaded] = useState(true);
	const [frameLoaded, setFrameLoaded] = useState(false);
	// Reference to the DOM node inside the iframe where SCORM content is rendered
	const mountNode = contentRef?.contentWindow?.document?.body;

	// Function to handle SCORM API initialization in the iframe window
	const handleScorm = (win: Window) => {
		const apiSettings = {
			...(scorm.data?.player || {}),
			lmsCommitUrl: false,
			mastery_override: true,
			logLevel,
		};
		const trackDetails = scorm.trackDetails || {};

		// Initializes SCORM 1.2 API in the iframe window
		function scorm12() {
			// Initialize the SCORM 1.2 API with the SCORM model data
			win.API = new Scorm12API(apiSettings);

			win.API.loadFromJSON(trackDetails); // Load existing tracking data into the SCORM API

			// `onCommit` will be called whenever SCORM attempts to save (commit) tracking data
			if (onCommit) {
				win.API.on('LMSCommit', () => {
					// Sends SCORM's `cmi` (Course Management Information) data to `onCommit`
					onCommit(win.API.cmi);
				});
			}
		}

		// Initializes SCORM 2004 API in the iframe window
		function scorm2004() {
			// Initialize the SCORM 2004 API with the SCORM model data
			win.API_1484_11 = new Scorm2004API(apiSettings);
			// Load existing tracking data for SCORM 2004
			win.API_1484_11.loadFromJSON(trackDetails);

			if (onCommit) {
				win.API_1484_11.on('Commit', () => {
					// Sends SCORM 2004 `cmi` data to `onCommit`
					onCommit(win.API_1484_11.cmi);
				});
			}
		}

		// Determine which SCORM version is being used and initialize the appropriate API
		if (scorm.data.version === 'scorm_12') {
			scorm12(); // Use SCORM 1.2 API
		} else if (scorm.data.version === 'scorm_2004') {
			scorm2004(); // Use SCORM 2004 API
		}
	};

	const checkDataLoaded = () => {
		const iframe = mountNode?.querySelector('iframe');
		const iframeBody = iframe?.contentDocument?.body;

		if (!iframeBody) return;

		const messageBox = iframeBody.querySelector('.message-box');

		// if the scorm data has been successfully loaded, a popup will be shown
		if (!messageBox) {
			setIsDataLoaded(false);
		}
	};

	// Hook that triggers when the iframe's content reference is ready
	useEffect(() => {
		if (!contentRef || !frameLoaded) {
			return; // If there is no contentRef yet, do nothing
		}

		// @TODO: refactor it
		const iframe = contentRef?.contentDocument?.body?.querySelector('iframe');
		if (iframe) {
			const iframeDocument = iframe.contentDocument;
			const iframeHead = iframeDocument?.head;

			if (iframeDocument && iframeHead) {
				const script = iframeDocument.createElement('script');
				// images are not displayed in mobile mode
				// to display images correctly, set user agent to desktop mode
				script.textContent = `
				Object.defineProperty(navigator, 'userAgent', {
					value: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/87.0.4280.66 Safari/537.36',
					configurable: true,
				});
			`;
				iframeHead?.appendChild(script);
			}
		}

		if (contentRef?.contentWindow) {
			handleScorm(contentRef.contentWindow); // Initialize SCORM API when iframe is ready
		}
	}, [contentRef, frameLoaded]);

	// Hook for loading SCORM content and optionally fetching additional data
	useEffect(() => {
		if (isAdminEnv) {
			clearScormLocalStorage();
		}

		const prepare = async () => {
			setLoading(true); // Set loading state to true while data is being fetched
			setFrameLoaded(false);
			let shouldRefreshFrame = false;
			try {
				await scorm.fetch(); // Fetch SCORM content data from the server
				shouldRefreshFrame = true;
				if (fetchData) {
					await fetchData(); // Optionally fetch additional data if fetchData is provided
				}
			} catch (e) {
				// SCORM data can be absent for a new or unavailable attempt.
			} finally {
				if (shouldRefreshFrame) {
					setUpdateKey((prev) => prev + 1);
				}
				setLoading(false); // Set loading to false once data is loaded
			}
		};
		prepare();
	}, [modified, isAdminEnv]);

	useEffect(() => {
		if (!isDataLoaded) {
			showReloadButton?.(true);

			if (isAdminEnv) {
				// If data not loaded, refresh the iframe.

				// Only for teacher side, because
				// if you update the iFrame, the commit event listener
				// for sending the scorm data
				// will be cancelled.
				setUpdateKey(modified + 1);
			}
		}
	}, [isDataLoaded, isAdminEnv]);

	const handleToggleFullScreen = () => {
		setIsFullScreen((prevIsFullScreen) => !prevIsFullScreen);
	};

	useEffect(() => {
		if (!enableOrientationLock || typeof window === 'undefined') {
			return;
		}

		const getIsPortrait = () => {
			if (typeof window === 'undefined') return false;
			if (window.matchMedia) {
				return window.matchMedia('(orientation: portrait)').matches;
			}
			return window.innerHeight > window.innerWidth;
		};

		const updateOrientation = () => setIsPortrait(getIsPortrait());
		updateOrientation();

		const mediaQuery = window.matchMedia?.('(orientation: portrait)');
		if (mediaQuery?.addEventListener) {
			mediaQuery.addEventListener('change', updateOrientation);
		} else if (mediaQuery?.addListener) {
			mediaQuery.addListener(updateOrientation);
		}

		window.addEventListener('orientationchange', updateOrientation);
		window.addEventListener('resize', updateOrientation);

		return () => {
			if (mediaQuery?.removeEventListener) {
				mediaQuery.removeEventListener('change', updateOrientation);
			} else if (mediaQuery?.removeListener) {
				mediaQuery.removeListener(updateOrientation);
			}
			window.removeEventListener('orientationchange', updateOrientation);
			window.removeEventListener('resize', updateOrientation);
		};
	}, [enableOrientationLock]);

	useEffect(() => {
		if (enableOrientationLock && isPortrait && isFullScreen) {
			setIsFullScreen(false);
		}
	}, [enableOrientationLock, isPortrait, isFullScreen]);

	const shouldShowOrientationOverlay = Boolean(
		enableOrientationLock && isPortrait && orientationHint,
	);

	const rendered = (
		<Async loading={loading}>
			<Wrapper>
				<FullScreenButton size="large" onClick={handleToggleFullScreen}>
					{isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
				</FullScreenButton>
				<WrapperIframe
					key={updateKey}
					srcDoc={OUTER_IFRAME_DOC}
					ref={setContentRef}
					onLoad={() => setFrameLoaded(true)}
				>
					{mountNode &&
						frameLoaded &&
						createPortal(
							<ScormIframe
								srcDoc={scorm.srcDoc} // SCORM content HTML document
								key={updateKey}
								style={{
									width: '100%',
									height: '100%',
									backgroundImage: scorm?.background ? `url("${scorm.background}")` : 'none',
									backgroundSize: 'contain',
									// if the height or width of the iFrame is less than 350px,
									// the controls disappear
									minWidth: 650,
									minHeight: 365,
								}}
								frameBorder="0"
								onLoad={checkDataLoaded}
							/>,
							mountNode,
						)}
				</WrapperIframe>
			</Wrapper>
		</Async>
	);

	return (
		<>
			<ModalElement isFullScreen={isFullScreen} withHeader={withHeader} isLarge={isLarge}>
				<StyledScormWrapper isOrientationBlocked={shouldShowOrientationOverlay}>
					{rendered}
				</StyledScormWrapper>
			</ModalElement>
			{shouldShowOrientationOverlay ? (
				<OrientationOverlay>
					<OrientationOverlayText>{orientationHint}</OrientationOverlayText>
				</OrientationOverlay>
			) : null}
		</>
	);
}

export default observer(ScormWrapper);
